import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { format } from "date-fns";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_11_officer = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Establishing of the controlling person of operating legal entities and partnerships both not quoted on the stock exchange (K)",
    de: "Feststellung des Kontrollinhabers an nicht börsenkotierten, operativ tätigen juristischen Personen und Personengesellschaften (K)",
  }),
  description: i18n.fixed({
    en: "for operating legal entities and partnerships that are contracting partner as well as analogously for operating legal entities and partnership that are beneficial owners",
    de: "bei operativ tätigen juristischen Personen und Personengesellschaften als Vertragspartner sowie sinngemäss bei operativ tätigen juristischen Personen und Personengesellschaften als wirtschaftlich Berechtigte",
  }),
  id: "vqf_902_11_officer",
  version: 1,
  config: VQF_902_11_officer(i18n),
});

export function VQF_902_11_officer(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  const today = format(new Date(), "yyyy-MM-dd");

  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Establishment of the controlling person (submitted by AML officer)",
      de: "Feststellung des Kontrollinhabers (durch den AML-Beauftragten eingereicht)",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Identity of the contracting partner",
          de: "Identität des Vertragspartners",
        }),
        description: i18n.fixed({
          en: "Name and address",
          de: "Name und Adresse",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_CONTRACTING_PARTNER,
            label: i18n.fixed({
              en: "Contracting partner",
              de: "Vertragspartner",
            }),
            type: "textArea",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Controlling person(s)",
          de: "Kontrollinhaber",
        }),
        fields: [
          {
            id: TalerFormAttributes.CONTROL_REASON,
            label: i18n.fixed({
              en: "Reason for control",
              de: "Grund der Kontrolle",
            }),
            type: "choiceStacked",
            choices: [
              {
                value: "HAS_25_MORE_RIGHTS",
                label: i18n.fixed({
                  en: "Holding 25% or more",
                  de: "Halten von 25% oder mehr",
                }),
                description: i18n.fixed({
                  en: "The person(s) listed below is/are holding 25% or more of the contracting partner's shares (capital shares or voting rights)",
                  de: "Die nachfolgend aufgeführte(n) Person(en) hält/halten am Vertragspartner Anteile (Kapital- oder Stimmrechtsanteile) von 25% oder mehr",
                }),
              },
              {
                value: "OTHER_WAY",
                label: i18n.fixed({ en: "Other way", de: "Andere Art" }),
                description: i18n.fixed({
                  en: "If the capital shares or voting rights cannot be determined or in case there are no capital shares or voting rights 25% or more, the contracting partner hereby declares that the person(s) listed below is/are controlling the contracting partner in other ways",
                  de: "Falls die Kapital- oder Stimmrechtsanteile nicht festgestellt werden können oder falls keine Kapital- oder Stimmrechtsanteile von 25% oder mehr bestehen, erklärt der Vertragspartner hiermit, dass die nachfolgend aufgeführte(n) Person(en) auf andere Weise die Kontrolle über den Vertragspartner ausübt/ausüben",
                }),
              },
              {
                value: "DIRECTOR",
                label: i18n.fixed({
                  en: "Managing director",
                  de: "Geschäftsführer",
                }),
                description: i18n.fixed({
                  en: "In case this/these person(s) cannot be determined or this/these person(s) does/do not exist, the contracting partner hereby declares that the person(s) listed below is/are the managing director(s)",
                  de: "Falls auch diese Person(en) nicht festgestellt werden kann/können oder diese Person(en) nicht besteht/bestehen, erklärt der Vertragspartner hiermit, dass die nachfolgend aufgeführte(n) Person(en) die Geschäftsführung ausübt/ausüben",
                }),
              },
            ],
            required: true,
          },
          {
            id: TalerFormAttributes.IDENTITY_LIST,
            label: i18n.fixed({
              en: "Controlling person(s)",
              de: "Kontrollinhaber",
            }),
            type: "array",
            validator(persons) {
              if (!persons || persons.length < 1) {
                return i18n.fixed({
                  en: "Can't be empty",
                  de: "Darf nicht leer sein",
                });
              }
              return undefined;
            },
            labelFieldId: TalerFormAttributes.FULL_NAME,
            fields: [
              {
                id: TalerFormAttributes.FULL_NAME,
                label: i18n.fixed({
                  en: "Full name",
                  de: "Vollständiger Name",
                }),
                type: "text",
                required: true,
              },
              {
                id: TalerFormAttributes.DOMICILE_ADDRESS,
                label: i18n.fixed({
                  en: "Actual address of domicile",
                  de: "Effektive Wohnsitzadresse",
                }),
                type: "textArea",
                required: true,
              },
            ],
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Fiduciary holding assets",
          de: "Treuhänderisches Halten von Vermögenswerten",
        }),
        fields: [
          {
            id: TalerFormAttributes.THIRD_PARTY_OWNERSHIP,
            label: i18n.fixed({
              en: "Is a third person the beneficial owner of the assets held in the account/securities account?",
              de: "Ist eine Drittperson an den auf dem Konto/Depot liegenden Vermögenswerten wirtschaftlich berechtigt?",
            }),
            required: true,
            type: "choiceHorizontal",
            choices: [
              {
                label: i18n.fixed({ en: "Yes", de: "Ja" }),
                value: true,
              },
              {
                label: i18n.fixed({ en: "No", de: "Nein" }),
                value: false,
              },
            ],
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
        description: i18n.fixed({
          en: "It is a criminal offence to deliberately provide false information on this form (article 251 of the Swiss Criminal Code, document forgery)",
          de: "Die vorsätzliche Angabe falscher Informationen in diesem Formular ist eine strafbare Handlung (Urkundenfälschung gemäss Artikel 251 des Schweizerischen Strafgesetzbuchs).",
        }),
        hide(root) {
          return root[TalerFormAttributes.SUBMITTED_BY] != "CUSTOMER";
        },
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The contracting partner hereby undertakes to inform automatically of any changes to the information contained herein.",
              de: "Der Vertragspartner verpflichtet sich, Änderungen jeweils unaufgefordert mitzuteilen.",
            }),
          },
          {
            id: TalerFormAttributes.SIGNATURE,
            label: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.SIGN_DATE,
            label: i18n.fixed({ en: "Date", de: "Datum" }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            required: true,
            defaultValue: today,
            disabled: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Signed Declaration",
          de: "Unterzeichnete Erklärung",
        }),
        description: i18n.fixed({
          en: "Signed declaration by the customer",
          de: "Vom Kunden unterzeichnete Erklärung",
        }),
        hide(root) {
          return root[TalerFormAttributes.SUBMITTED_BY] != "AML_OFFICER";
        },
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The uploaded document must contain the customer's signature on the beneficial owner declaration.",
              de: "Das hochgeladene Dokument muss die Unterschrift des Kunden auf der Erklärung zum wirtschaftlich Berechtigten enthalten.",
            }),
          },
          {
            id: TalerFormAttributes.ATTACHMENT_SIGNED_DOCUMENT,
            label: i18n.fixed({
              en: "Signed Document",
              de: "Unterzeichnetes Dokument",
            }),
            type: "file",
            accept: "application/pdf",
            required: true,
          },
        ],
      },
    ],
  };
}
